import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import cs from "@/locales/cs.json";
import sk from "@/locales/sk.json";
import hu from "@/locales/hu.json";
import en from "@/locales/en.json";

const SUPPORTED_LANGS = ["cs", "sk", "hu", "en"] as const;
export type SupportedLang = (typeof SUPPORTED_LANGS)[number];

export function isSupportedLang(
  value: string | null | undefined,
): value is SupportedLang {
  return (
    typeof value === "string" &&
    (SUPPORTED_LANGS as readonly string[]).includes(value)
  );
}

/**
 * Append the current UI locale as a `loc=` query parameter to the given URL.
 * Always appends — even for the default language (`cs`) — so the integration
 * host can rely on `loc` always being present in the URL.
 *
 * Reads the locale from sessionStorage (`praetor:lang`), which is kept in sync
 * by LocaleProvider. Falls back to `cs` if unset, unsupported, or sessionStorage
 * is unavailable (e.g. during SSR).
 *
 * Assumes the caller passes a URL without an existing `loc=` parameter.
 */
export function appendLocParam(url: string): string {
  let lang: SupportedLang = "cs";
  try {
    const stored = sessionStorage.getItem("praetor:lang");
    if (isSupportedLang(stored)) lang = stored;
  } catch { /* sessionStorage unavailable — keep default */ }

  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}loc=${lang}`;
}

/**
 * Resolve initial language from URL search params and sessionStorage.
 * Called once at module init time. The LocaleProvider keeps
 * i18next.language in sync with URL/sessionStorage changes afterwards.
 */
function resolveInitialLang(): SupportedLang {
  if (typeof window !== "undefined") {
    try {
      const loc = new URL(window.location.href).searchParams.get("loc");
      if (isSupportedLang(loc)) return loc;
    } catch {
      /* URL parsing failed */
    }

    try {
      const stored = sessionStorage.getItem("praetor:lang");
      if (isSupportedLang(stored)) return stored;
    } catch {
      /* sessionStorage unavailable */
    }
  }

  return "cs";
}

i18n.use(initReactI18next).init({
  resources: {
    cs: { translation: cs },
    sk: { translation: sk },
    hu: { translation: hu },
    en: { translation: en },
  },
  lng: resolveInitialLang(),
  fallbackLng: "cs",
  supportedLngs: [...SUPPORTED_LANGS],

  // Keys use dot notation ("login.title") as flat keys, NOT nested paths
  keySeparator: false,
  nsSeparator: false,

  interpolation: {
    escapeValue: false, // React already escapes
  },

  // Resources are bundled statically — init synchronously
  initAsync: false,

  returnNull: false,
});

export default i18n;
